<script setup>
import { ref, computed, onMounted } from 'vue'
import { listRole } from '@/api/system/role'

const loading = ref(false)
const query = ref({ roleName: '', roleKey: '', status: '' })
const showSearch = ref(true)
const statusOptions = [
  { label: 'Enabled', value: '0' },
  { label: 'Disabled', value: '1' },
]

const roles = ref([])
const filteredRoles = computed(() => roles.value)

const fetchRoles = async () => {
  loading.value = true
  try {
    const res = await listRole({
      pageNum: 1,
      pageSize: 200,
      roleName: query.value.roleName || undefined,
      roleKey: query.value.roleKey || undefined,
      status: query.value.status || undefined,
    })
    roles.value = res?.rows || []
  } catch (err) {
    console.error('Failed to load roles', err)
    roles.value = []
  } finally {
    loading.value = false
  }
}

const handleQuery = () => fetchRoles()
const resetQuery = () => {
  query.value = { roleName: '', roleKey: '', status: '' }
  fetchRoles()
}

const toggleStatus = (item) => {
  item.status = item.status === '0' ? '1' : '0'
}

onMounted(() => {
  fetchRoles()
})
</script>

<template>
  <v-container fluid class="page-shell">
    <v-row class="text-center mb-4">
      <v-col cols="12">
        <h1 class="title font-weight-bold">Role Management</h1>
        <div class="headline-border"></div>
      </v-col>
    </v-row>

    <v-card class="section-card mb-4">
      <v-card-title>Filters</v-card-title>
      <v-card-text>
        <v-form v-if="showSearch">
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field v-model="query.roleName" label="Role Name" dense clearable hide-details />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="query.roleKey" label="Role Key" dense clearable hide-details />
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="query.status" :items="statusOptions" item-title="label" item-value="value" label="Status" dense clearable hide-details />
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center flex-wrap gap-3">
              <v-btn size="small" color="primary" @click="handleQuery">Search</v-btn>
              <v-btn size="small" variant="tonal" color="primary" @click="resetQuery">Reset</v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-card class="section-card">
      <v-card-title>Role List</v-card-title>
      <v-data-table
        :items="filteredRoles"
        :headers="[
          { title: 'Role ID', key: 'roleId', width: 110 },
          { title: 'Role Name', key: 'roleName' },
          { title: 'Role Key', key: 'roleKey' },
          { title: 'Display Order', key: 'roleSort', width: 130 },
          { title: 'Status', key: 'status', width: 140 },
          { title: 'Create Time', key: 'createTime', width: 180 },
        ]"
        class="elevation-1 monitoring-table"
        :loading="loading"
        disable-sort
        disable-pagination
        hide-default-footer
        density="comfortable"
      >
        <template #item.status="{ item }">
          <div class="d-flex align-center">
            <v-chip :color="item.status === '0' ? 'success' : 'grey'" size="small" variant="flat" class="mr-2">
              {{ item.status === '0' ? 'Enabled' : 'Disabled' }}
            </v-chip>
            <v-switch density="compact" :model-value="item.status === '0'" @change="toggleStatus(item)" hide-details inset />
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.page-shell {
  padding: 8px 18px 28px;
}

.title {
  font-size: 1.1rem;
}

.headline-border {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4f46e5, #06b6d4);
  margin: 12px auto 0;
  border-radius: 999px;
}

.section-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  padding: 10px 12px 14px;
}
</style>
